import type { ColumnDef } from '@talos/kyoko';
import { CurrencyIcon, HStack, NumberVariants, Text, useCurrenciesContext, VStack } from '@talos/kyoko';
import { Sparkline } from '@talos/kyoko/src/components/Watchlist/Sparkline';
import type { ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';
import styled from 'styled-components';
import { formatRelativePercent, type SignalsValueFormatter } from '../core';
import type { RelativePerformanceTableRow } from './Comparison.types';

interface UseComparisonTableColumnsProps {
  formatMetricValue: SignalsValueFormatter;
  rowColors: ReadonlyMap<string, string>;
  metricDisplayName: string;
}

export function useComparisonTableColumns({
  formatMetricValue,
  rowColors,
  metricDisplayName,
}: UseComparisonTableColumnsProps) {
  const { currenciesBySymbol } = useCurrenciesContext();

  return useMemo<ColumnDef<RelativePerformanceTableRow>[]>(
    () => [
      {
        type: 'custom',
        id: 'legendColor',
        title: '',
        params: {
          width: 32,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            const color = data ? rowColors.get(data.cmAssetId) : undefined;
            if (!color) {
              return null;
            }
            return <ColorDot $color={color} />;
          },
        },
      },
      {
        type: 'custom',
        id: 'asset',
        title: 'Asset',
        params: {
          width: 160,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (!data) {
              return null;
            }
            const currency = currenciesBySymbol.get(data.talosSymbol);
            return (
              <HStack gap="spacing8" h="100%" overflow="hidden">
                <CurrencyIcon currency={data.talosSymbol} size={20} />
                <VStack gap="spacing2" alignItems="flex-start" overflow="hidden">
                  <Text weight="fontWeightMedium" whiteSpace="nowrap">
                    {currency?.Description ?? data.talosSymbol}
                  </Text>
                  <Text color="colorTextSubtle" whiteSpace="nowrap" size="fontSizeSmall">
                    {data.talosSymbol}
                  </Text>
                </VStack>
              </HStack>
            );
          },
        },
      },
      {
        type: 'custom',
        id: 'metric',
        title: 'Metric',
        params: {
          width: 120,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) =>
            data ? <Text>{metricDisplayName}</Text> : null,
        },
      },
      {
        type: 'custom',
        id: 'startValue',
        title: 'Start Value',
        params: {
          width: 120,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (data?.startValue == null) {
              return <Text color="colorTextSubtle">—</Text>;
            }
            return <Text>{formatMetricValue(data.startValue)}</Text>;
          },
        },
      },
      {
        type: 'custom',
        id: 'endValue',
        title: 'End Value',
        params: {
          width: 120,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (data?.endValue == null) {
              return <Text color="colorTextSubtle">—</Text>;
            }
            return <Text>{formatMetricValue(data.endValue)}</Text>;
          },
        },
      },
      {
        type: 'custom',
        id: 'change',
        title: 'Change',
        params: {
          width: 160,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (!data?.hasData || data.changePercent == null) {
              return <Text color="colorTextSubtle">—</Text>;
            }
            const isPositive = data.changePercent >= 0;
            const variant = isPositive ? NumberVariants.Positive : NumberVariants.Negative;
            return (
              <VStack gap="spacing2" h="100%" w="100%" alignItems="center" justifyContent="center">
                <Text color={isPositive ? 'colorTextPositive' : 'colorTextNegative'} whiteSpace="nowrap">
                  {formatRelativePercent(data.changePercent)}
                </Text>
                {data.sparklineData.length > 0 && (
                  <Sparkline data={data.sparklineData} variant={variant} h="24px" w="100%" />
                )}
              </VStack>
            );
          },
        },
      },
      {
        type: 'custom',
        id: 'minPercent',
        title: 'Min %',
        params: {
          width: 90,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (!data?.hasData || data.minPercent == null) {
              return <Text color="colorTextSubtle">—</Text>;
            }
            return <Text color="colorTextNegative">{formatRelativePercent(data.minPercent)}</Text>;
          },
        },
      },
      {
        type: 'custom',
        id: 'maxPercent',
        title: 'Max %',
        params: {
          width: 90,
          cellRenderer: ({ data }: ICellRendererParams<RelativePerformanceTableRow>) => {
            if (!data?.hasData || data.maxPercent == null) {
              return <Text color="colorTextSubtle">—</Text>;
            }
            return <Text color="colorTextPositive">{formatRelativePercent(data.maxPercent)}</Text>;
          },
        },
      },
    ],
    [currenciesBySymbol, formatMetricValue, rowColors, metricDisplayName]
  );
}

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;
